import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import localizedFormat from "dayjs/plugin/localizedFormat";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(localizedFormat);

// Escape CSV field values
const escapeCsvValue = (value) => {
  if (value == null) return "";
  const str = String(value);
  if (str.includes(",") || str.includes('"') || str.includes("\n")) {
    return `"${str.replace(/"/g, '""')}"`; // Double quotes inside fields must be escaped
  }
  return str;
};

// Parse CSV row while respecting quoted values (e.g. "A, B")
const parseCsvRow = (row = "") => {
  const fields = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < row.length; i++) {
    const ch = row[i];
    const next = row[i + 1];

    if (ch === '"') {
      // Escaped quote inside quoted value ("")
      if (inQuotes && next === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (ch === "," && !inQuotes) {
      fields.push(current);
      current = "";
      continue;
    }

    current += ch;
  }

  fields.push(current);
  return fields;
};

const downloadCsv = (data, location, fileName) => {
  const generateTitle = (path) => {
    if (!path) return "data-export";
    let title = path.split("?")[0].replace(/\//g, "-").replace(/^-|-$/g, "");
    return title || "data-export";
  };

  const resolveDownloadBaseName = () => {
    if (fileName != null && String(fileName).trim() !== "") {
      let base = String(fileName).trim().replace(/\.csv$/i, "");
      base = base
        .replace(/[/\\?%*:|"<>]/g, "-")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "");
      return base || "data-export";
    }
    const path =
      location?.pathname != null
        ? `${location.pathname}${location.search || ""}`
        : "";
    return generateTitle(path);
  };

  const generatedTitle = resolveDownloadBaseName();
  const timezoneVal = sessionStorage.getItem("timezone") || "UTC";

  const rows = Array.isArray(data) ? data : data.split("\n");

  if (rows.length === 0) return; // No data to download

  // Prepare CSV
  const csvRows = [];

  const headers = parseCsvRow(rows[0]); // Header columns
  csvRows.push(headers.map(escapeCsvValue).join(",")); // Escape headers safely

  // Find time column indexes
  const timeColumnIndices = headers
    .map((column, index) => (column.includes("Time") ? index : -1))
    .filter((index) => index !== -1);

  // Process data rows
  for (let i = 1; i < rows.length; i++) {
    if (!rows[i].trim()) continue; // skip empty lines
    const rawValues = parseCsvRow(rows[i]);

    const rowValues = rawValues.map((value, index) => {
      if (timeColumnIndices.includes(index) && value && value !== "0") {
        return dayjs.unix(value).tz(timezoneVal).format("YYYY-MM-DD HH:mm:ss");
      }
      return value;
    });

    csvRows.push(rowValues.map(escapeCsvValue).join(","));
  }

  const csvContent = "\uFEFF" + csvRows.join("\n"); // BOM + CSV

  // Create download link
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.setAttribute("href", url);
  link.setAttribute("download", `${generatedTitle}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
};

export default downloadCsv;
